'use client';

import { useState } from "react"
import { 
  Drawer, 
  DrawerContent, 
  DrawerDescription, 
  DrawerHeader, 
  DrawerTitle,
  Button,
  Textarea,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from '@city-sage/aurum-avis-labs-components'
import { Bold, Italic, List, Link, Eye, Save, X } from "lucide-react"

interface MarkupEditorSheetProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  value: string
  onSave: (value: string) => void
}

export function MarkupEditorSheet({ 
  isOpen, 
  onOpenChange, 
  value, 
  onSave 
}: MarkupEditorSheetProps) {
  const [content, setContent] = useState(value)
  const [activeTab, setActiveTab] = useState<"edit" | "preview">("edit")

  const handleSave = () => {
    onSave(content)
  }

  const insertMarkdown = (before: string, after: string = '') => {
    const textarea = document.querySelector('#markdown-editor') as HTMLTextAreaElement
    if (!textarea) return

    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const selectedText = content.substring(start, end)
    const newText = content.substring(0, start) + before + selectedText + after + content.substring(end)
    
    setContent(newText)
    
    // Focus and set cursor position
    setTimeout(() => {
      textarea.focus()
      const newCursorPos = start + before.length + selectedText.length + after.length
      textarea.setSelectionRange(newCursorPos, newCursorPos)
    }, 0)
  }

  const renderPreview = (text: string) => {
    // Basic markdown rendering for preview
    return text
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/^### (.*$)/gim, '<h3>$1</h3>')
      .replace(/^## (.*$)/gim, '<h2>$1</h2>')
      .replace(/^# (.*$)/gim, '<h1>$1</h1>')
      .replace(/^\* (.*$)/gim, '<li>$1</li>')
      .replace(/^\- (.*$)/gim, '<li>$1</li>')
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
      .replace(/\n/g, '<br>')
  }

  return (
    <Drawer open={isOpen} onOpenChange={onOpenChange}>
      <DrawerContent className="h-[90dvh] flex flex-col">
        <DrawerHeader className="mb-4">
          <DrawerTitle>Markup Editor</DrawerTitle>
          <DrawerDescription>
            Edit your description with markdown formatting
          </DrawerDescription>
        </DrawerHeader>

        <div className="flex-1 flex flex-col space-y-4 px-6 pb-6">
          {/* Toolbar */}
          <div className="flex items-center gap-2 p-2 border rounded-md bg-gray-50">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => insertMarkdown('**', '**')}
              title="Bold"
            >
              <Bold className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => insertMarkdown('*', '*')}
              title="Italic"
            >
              <Italic className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => insertMarkdown('- ', '')}
              title="List Item"
            >
              <List className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => insertMarkdown('[Link Text](', ')')}
              title="Link"
            >
              <Link className="h-4 w-4" />
            </Button>
          </div>

          {/* Editor Tabs */}
          <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as "edit" | "preview")} className="flex-1">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="edit">Edit</TabsTrigger>
              <TabsTrigger value="preview">Preview</TabsTrigger>
            </TabsList>
            
            <TabsContent value="edit" className="flex-1 mt-4">
              <Textarea
                id="markdown-editor"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Write your markdown here..."
                className="min-h-[400px] font-mono text-sm resize-none"
              />
            </TabsContent>
            
            <TabsContent value="preview" className="flex-1 mt-4">
              <div 
                className="min-h-[400px] p-4 border rounded-md bg-white prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: renderPreview(content) }}
              />
            </TabsContent>
          </Tabs>

          {/* Action Buttons */}
          <div className="flex items-center justify-between gap-2 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="gap-2"
            >
              <X className="h-4 w-4" />
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSave}
              className="gap-2"
            >
              <Save className="h-4 w-4" />
              Save Changes
            </Button>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  )
} 